'use client';

import { motion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { CardEntrance } from './card-entrance';
import { DesktopCard } from './desktop-card';
import { INTERACTIVE_OFFSET, SLIDE_TRANSITION } from './constants';
import {
  entranceSlot,
  fanTransform,
  productAt,
  windowIndices,
} from './geometry';
import { NavArrow } from './nav-arrow';
import { useEntranceReveal } from './useEntranceReveal.hook';
import { usePrevious } from './usePrevious.hook';
import { useRovingCarousel } from './useRovingCarousel.hook';

// 데스크탑 부채꼴 캐러셀. 키보드 상호작용(roving)은 useRovingCarousel 훅이 담당하고,
// 여기선 원호 배치 렌더링에만 집중한다.
// 화면 밖 카드는 useRovingCarousel 이 참조하는 data-* 표식(data-card/data-center/data-nav)을 단다.
export function DesktopFan({
  active,
  goTo,
  prev,
  next,
  entered,
}: {
  active: number;
  goTo: (i: number) => void;
  prev: () => void;
  next: () => void;
  // 스크롤 진입으로 카드 등장을 시작할지
  entered: boolean;
}) {
  const t = useTranslations('Products.solutionFinder');
  const prevActive = usePrevious(active);
  const { containerRef, onKeyDown } = useRovingCarousel({ active, goTo });
  // 최초 등장 대상 카드 집합(마운트 시점 캡처) — 이후 이동으로 들어온 카드는 등장 제외
  const shouldRunEntrance = useEntranceReveal(active, INTERACTIVE_OFFSET);

  return (
    // 세로(아래)만 클립: 원을 따라 내려가 스테이지를 넘는 카드만 가림.
    // 가로는 자르지 않음(카드가 좌우로 자연스럽게 뻗어나가고 뷰포트에서만 잘림).
    <div
      ref={containerRef}
      onKeyDown={onKeyDown}
      role="group"
      aria-roledescription="carousel"
      className="relative h-[712px] w-full"
      style={{ clipPath: 'inset(-2000px -2000px 0px -2000px)' }}
    >
      {windowIndices(active).map(j => {
        const offset = j - active;
        const { x, y, rotate, zIndex } = fanTransform(offset);
        // 신규 카드 초기 위치 = 직전 active 기준 offset(윈도우 밖 → 밀려 들어옴)
        const init = fanTransform(j - prevActive);
        const interactive = Math.abs(offset) <= INTERACTIVE_OFFSET;
        const isCenter = offset === 0;
        return (
          <motion.div
            key={j}
            data-card=""
            data-center={isCenter ? '' : undefined}
            className="absolute left-1/2 top-0 -ml-[180px]"
            style={{ zIndex, pointerEvents: interactive ? 'auto' : 'none' }}
            initial={{ x: init.x, y: init.y, rotate: init.rotate }}
            animate={{ x, y, rotate }}
            transition={SLIDE_TRANSITION}
          >
            <CardEntrance
              run={shouldRunEntrance(j)}
              entered={entered}
              slot={entranceSlot(offset, INTERACTIVE_OFFSET)}
            >
              <DesktopCard
                product={productAt(j)}
                onSelect={() => goTo(j)}
                interactive={interactive}
                keyboardFocusable={isCenter}
              />
            </CardEntrance>
          </motion.div>
        );
      })}

      {/* 화살표 — 중앙 카드(높이 488) 하단에서 72px 아래 (488+72=560).
          roving: Tab 이 아닌 ↑/↓·← → 로만 접근하므로 tabIndex -1 */}
      <div className="absolute left-1/2 top-[560px] z-40 flex -translate-x-1/2 items-center gap-[23px]">
        <NavArrow
          direction="prev"
          onClick={prev}
          label={t('carousel.prev')}
          tabIndex={-1}
          large
        />
        <NavArrow
          direction="next"
          onClick={next}
          label={t('carousel.next')}
          tabIndex={-1}
          large
        />
      </div>
    </div>
  );
}
